import React, {useState} from "react";
import {FaEye, FaEyeSlash} from "react-icons/fa";
import {FiX} from "react-icons/fi";
import toast from "react-hot-toast";
import API from "../api/config";
import Logo from "../assets/QRscriptsLogo1.png"; // Imported Logo

const AdminLogin = () => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [show, setShow] = useState(false);
    const [loading, setLoading] = useState(false);

    // Forgot password states
    const [showForgot, setShowForgot] = useState(false);
    const [otp, setOtp] = useState("");
    const [step, setStep] = useState(1); // 1 = email, 2 = otp + new password
    const [newPassword, setNewPassword] = useState("");

    const [showNewPassword, setShowNewPassword] = useState(false);

    // 🔐 ADMIN LOGIN
    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setLoading(true);
            const {data} = await API.post("/admin/auth/login", {
                email,
                password,
            });
            localStorage.setItem("admin_token", data.token);
            toast.success("Login successful");
            window.location.href = "/dashboard";
        } catch (err) {
            toast.error(err.response?.data?.message || "Login failed");
        } finally {
            setLoading(false);
        }
    };

    // 📧 SEND OTP
    const sendOtp = async () => {
        if (!email) return toast.error("Email is required");
        try {
            setLoading(true);
            await API.post("/admin/auth/forgot-password", {email});
            toast.success("OTP sent to email");
            setStep(2);
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to send OTP");
        } finally {
            setLoading(false);
        }
    };

    // 🔁 RESET PASSWORD
    const resetPassword = async () => {
        if (!otp || !newPassword) {
            return toast.error("OTP and new password required");
        }
        try {
            setLoading(true);
            await API.post("/admin/auth/reset-password", {
                email,
                otp,
                newPassword,
            });
            toast.success("Password updated. Please login.");
            setShowForgot(false);
            setStep(1);
            setOtp("");
            setNewPassword("");
        } catch (err) {
            toast.error(err.response?.data?.message || "Reset failed");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-gradient-to-b from-pink-50 to-white">
            {/* ================= HEADER ================= */}
            <header className="bg-white border-b border-pink-100 shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 -mr-20 -mt-20 w-64 h-64 bg-pink-50 rounded-full blur-3xl opacity-50"></div>
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between relative z-10">
                    <div className="flex items-center gap-4">
                        <div className="bg-gradient-to-br from-pink-100 to-white p-2 rounded-2xl shadow-sm border border-pink-50">
                            <a href="https://www.qrscripts.in/" target="_blank" rel="noopener noreferrer">
                                <img
                                    src={Logo}
                                    alt="QR Scripts"
                                    className="h-10 w-auto cursor-pointer hover:scale-105 transition-transform"
                                />
                            </a>
                        </div>
                        <div className="leading-tight">
                            <h1 className="bg-gradient-to-r from-pink-600 via-pink-500 to-purple-700 bg-clip-text text-transparent font-extrabold text-xl leading-tight">
                                QR Scripts
                            </h1>
                            <p className="text-slate-500 text-[10px] uppercase tracking-wider font-bold">
                                Admin Control Panel
                            </p>
                        </div>
                    </div>
                    <div className="hidden sm:flex items-center gap-6">
                        <nav className="flex gap-4 text-xs">
                            <a
                                href="https://www.qrscripts.in/privacy-policy"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-500 hover:text-pink-600 transition font-medium"
                            >
                                Privacy Policy
                            </a>
                            <a
                                href="https://www.qrscripts.in/terms-and-condition"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-slate-500 hover:text-pink-600 transition font-medium"
                            >
                                Terms
                            </a>
                        </nav>
                        <div
                            className="h-2 w-2 rounded-full bg-pink-500 animate-pulse"
                            title="Secure Admin Portal"
                        ></div>
                    </div>
                </div>
            </header>

            {/* ================= MAIN CONTENT ================= */}
            <main className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md bg-white rounded-3xl p-8 shadow-xl shadow-pink-500/10 border border-pink-50">
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Admin Login</h2>
                    <p className="text-sm text-slate-500 mb-6">Access the management dashboard</p>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                Admin Email
                            </label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="admin@qrscripts.com"
                                className="w-full rounded-xl border border-slate-200 px-4 py-3 mt-1 focus:ring-2 focus:ring-pink-400 outline-none transition"
                                required
                            />
                        </div>

                        <div>
                            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                                Password
                            </label>
                            <div className="relative mt-1">
                                <input
                                    type={show ? "text" : "password"}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="w-full rounded-xl border border-slate-200 px-4 py-3 pr-10 focus:ring-2 focus:ring-pink-400 outline-none transition"
                                    required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShow(!show)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-pink-500 transition"
                                >
                                    {show ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                </button>
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full bg-pink-600 hover:bg-pink-700 text-white py-3 rounded-xl font-bold shadow-lg shadow-pink-500/20 transition-all active:scale-95 disabled:opacity-50"
                        >
                            {loading ? "Logging in…" : "Sign In to Dashboard"}
                        </button>
                    </form>

                    <p
                        onClick={() => setShowForgot(true)}
                        className="text-center text-sm font-semibold text-pink-600 mt-6 cursor-pointer hover:underline"
                    >
                        Forgot password?
                    </p>
                </div>
            </main>

            {/* ================= FOOTER ================= */}
            <footer className="text-center py-6 text-xs text-slate-400 font-medium tracking-wide">
                © {new Date().getFullYear()} QR Scripts · Admin Management Portal
            </footer>

            {/* RESET PASSWORD MODAL */}
            {showForgot && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl animate-in fade-in zoom-in duration-200 relative">
                        {/* Decorative Gradient Bar at the top */}

                        {/* Close Button Icon */}
                        <button
                            onClick={() => {
                                setShowForgot(false);
                                setStep(1);
                            }}
                            className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
                        >
                            <FiX size={20} />
                        </button>

                        <div className="p-8">
                            <h2 className="text-2xl font-bold text-slate-800 mb-2">Reset Password</h2>
                            <p className="text-xs text-slate-500 mb-6 leading-relaxed">
                                {step === 1
                                    ? "Enter your admin email to receive a secure verification code."
                                    : "Enter the code sent to your email and choose a strong new password."}
                            </p>

                            {step === 1 && (
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                                            Admin Email
                                        </label>
                                        <input
                                            type="email"
                                            value={email}
                                            onChange={(e) => setEmail(e.target.value)}
                                            placeholder="Confirm your email"
                                            className="w-full border border-slate-200 rounded-xl px-4 py-3 mt-1 focus:ring-2 focus:ring-pink-400 outline-none transition-all shadow-sm"
                                        />
                                    </div>
                                    <button
                                        onClick={sendOtp}
                                        disabled={loading}
                                        className="w-full bg-pink-600 hover:bg-pink-700 text-white py-3 rounded-xl font-bold shadow-lg shadow-pink-500/20 transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        {loading ? "Sending..." : "Send OTP"}
                                    </button>
                                </div>
                            )}

                            {step === 2 && (
                                <div className="space-y-4">
                                    {/* Verification Code Input */}
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                                            Verification Code
                                        </label>
                                        <input
                                            value={otp}
                                            onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
                                            maxLength={6}
                                            placeholder="0 0 0 0 0"
                                            className="w-full border border-slate-200 rounded-xl px-4 py-3 mt-1 text-center tracking-[0.5em] text-lg font-bold focus:ring-2 focus:ring-pink-400 outline-none shadow-sm"
                                        />
                                    </div>

                                    {/* New Password Input with Eye Icon */}
                                    <div>
                                        <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">
                                            New Password
                                        </label>
                                        <div className="relative mt-1">
                                            <input
                                                type={showNewPassword ? "text" : "password"}
                                                value={newPassword}
                                                onChange={(e) => setNewPassword(e.target.value)}
                                                placeholder="••••••••"
                                                className="w-full border border-slate-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-pink-400 outline-none shadow-sm transition-all"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                                className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-pink-500 transition-colors focus:outline-none"
                                            >
                                                {showNewPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
                                            </button>
                                        </div>
                                    </div>

                                    {/* Update Button */}
                                    <button
                                        onClick={resetPassword}
                                        disabled={loading}
                                        className="w-full bg-pink-600 hover:bg-pink-700 text-white py-3 rounded-xl font-bold shadow-lg shadow-pink-500/20 transition-all active:scale-95 disabled:opacity-50"
                                    >
                                        {loading ? "Updating..." : "Update Password"}
                                    </button>
                                </div>
                            )}

                            <button
                                onClick={() => {
                                    setShowForgot(false);
                                    setStep(1);
                                }}
                                className="w-full mt-6 text-sm font-semibold text-slate-400 hover:text-pink-600 transition-colors"
                            >
                                Back to Login
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminLogin;
